'use client';

import { Box, Typography } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { mapRawToBuzzBridgeRequest, type BuzzBridgeRequestRow } from '../utils/mapBuzzBridgeRequest';
import { CampaignThreadUnreadBadge } from '../CampaignThreadUnreadBadge';

const SURFACE_CARD = '#1a1919';
const GOLD = '#ffc965';
const ON_VARIANT = '#adaaaa';
const PENDING_TEAL_BG = 'rgba(0,253,193,0.1)';
const PENDING_TEAL_TEXT = '#00fdc1';
const RADIUS_FULL = '0.75rem';

interface InvitationsListProps {
    invitations: unknown[];
    onAccept: (id: string) => void;
    onReject: (id: string) => void;
}

export function InvitationsList({ invitations, onAccept, onReject }: InvitationsListProps) {
    const rows: BuzzBridgeRequestRow[] = invitations
        .map(mapRawToBuzzBridgeRequest)
        .filter((r): r is BuzzBridgeRequestRow => r != null);

    const metaIconSx = { fontSize: 18, color: ON_VARIANT };

    return (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', minWidth: 0 }}>
            {rows.map(inv => {
                const pending = inv.status === 'pending';
                const accepted = inv.status === 'accepted';
                const rejected = inv.status === 'rejected';

                const statusChip = accepted
                    ? {
                          label: 'Accepted',
                          icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
                          bg: 'rgba(0, 253, 193, 0.12)',
                          color: '#00fdc1',
                      }
                    : rejected
                    ? {
                          label: 'Declined',
                          icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} />,
                          bg: 'rgba(255, 115, 81, 0.12)',
                          color: '#ff7351',
                      }
                    : {
                          label: 'New Invitation',
                          icon: <MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />,
                          bg: PENDING_TEAL_BG,
                          color: PENDING_TEAL_TEXT,
                      };

                return (
                    <Box
                        key={inv.id}
                        sx={{
                            bgcolor: SURFACE_CARD,
                            borderRadius: 2,
                            p: 2.5,
                            border: pending ? '1px solid rgba(0,253,193,0.12)' : '1px solid rgba(255,255,255,0.04)',
                            transition: 'background-color 0.2s',
                        }}>
                        {/* Top row: status chip + action badge */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 2,
                                mb: 2,
                            }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: RADIUS_FULL,
                                    bgcolor: statusChip.bg,
                                    color: statusChip.color,
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 700,
                                }}>
                                {statusChip.icon}
                                {statusChip.label}
                            </Box>

                            {pending ? (
                                <Box
                                    sx={{
                                        px: 1.75,
                                        py: 0.5,
                                        borderRadius: RADIUS_FULL,
                                        border: `1px solid ${GOLD}`,
                                        color: GOLD,
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        flexShrink: 0,
                                    }}>
                                    Awaiting your response
                                </Box>
                            ) : accepted ? (
                                <Box
                                    sx={{
                                        px: 1.75,
                                        py: 0.5,
                                        borderRadius: RADIUS_FULL,
                                        border: '1px solid rgba(0,253,193,0.45)',
                                        color: '#00fdc1',
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                    }}>
                                    Joined
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        px: 1.75,
                                        py: 0.5,
                                        borderRadius: RADIUS_FULL,
                                        border: '1px solid rgba(255,115,81,0.45)',
                                        color: '#ff7351',
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                    }}>
                                    Declined
                                </Box>
                            )}
                        </Box>

                        {/* Title + company */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 700,
                                    fontSize: 17,
                                    color: '#fff',
                                    lineHeight: 1.3,
                                }}>
                                {inv.title}
                            </Typography>
                            <CampaignThreadUnreadBadge count={inv.unreadMessageCount} size="sm" />
                        </Box>
                        {inv.company ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: ON_VARIANT,
                                    mb: inv.description ? 1 : 2,
                                }}>
                                {inv.company}
                            </Typography>
                        ) : null}
                        {inv.description ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: ON_VARIANT,
                                    mb: 2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    lineHeight: 1.5,
                                }}>
                                {inv.description}
                            </Typography>
                        ) : null}

                        {/* Meta row */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                columnGap: 2.5,
                                rowGap: 1,
                                mb: pending ? 2.5 : 0,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <AttachMoneyIcon sx={metaIconSx} />
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: ON_VARIANT }}>
                                    {inv.budget}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CalendarTodayIcon sx={metaIconSx} />
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: ON_VARIANT }}>
                                    {inv.deadline}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <AccessTimeIcon sx={metaIconSx} />
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: ON_VARIANT }}>
                                    {inv.appliedDate !== '—' ? `Received ${inv.appliedDate}` : 'Recently received'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Accept / Decline buttons for pending invitations */}
                        {pending && (
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box
                                    component="button"
                                    onClick={() => onAccept(inv.id)}
                                    sx={{
                                        flex: 1,
                                        py: 1.25,
                                        borderRadius: RADIUS_FULL,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        fontSize: 13,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        color: '#392700',
                                        background: `linear-gradient(to top right, ${GOLD}, #feb700)`,
                                        transition: 'filter 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            filter: 'brightness(1.08)',
                                            boxShadow: '0 0 20px rgba(255,201,101,0.3)',
                                        },
                                    }}>
                                    Accept
                                </Box>
                                <Box
                                    component="button"
                                    onClick={() => onReject(inv.id)}
                                    sx={{
                                        flex: 1,
                                        py: 1.25,
                                        borderRadius: RADIUS_FULL,
                                        border: '1px solid rgba(255,115,81,0.35)',
                                        cursor: 'pointer',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        fontSize: 13,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        color: '#ff7351',
                                        bgcolor: 'rgba(255,115,81,0.08)',
                                        transition: 'background-color 0.2s, border-color 0.2s',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,115,81,0.15)',
                                            borderColor: 'rgba(255,115,81,0.6)',
                                        },
                                    }}>
                                    Decline
                                </Box>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
